import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { type ButtonHTMLAttributes, forwardRef } from 'react';
import { cn } from '../lib/cn';

/** Receitas em skill frontend-design → references/components.md §Botões. */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded border border-transparent font-medium transition-[background,box-shadow,color,border-color] duration-(--dur-fast) ease-app disabled:pointer-events-none disabled:opacity-50 focus-visible:outline-none focus-visible:shadow-ring [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary: 'bg-accent text-accent-fg hover:bg-accent-hover hover:shadow-glow',
        secondary: 'bg-surface-2 border-border-strong text-text-1 hover:bg-surface-3',
        ghost: 'bg-transparent text-text-2 hover:bg-surface-2 hover:text-text-1',
        outline: 'bg-transparent border-accent-border text-accent hover:bg-accent-soft',
        danger:
          'bg-danger-soft border-[color-mix(in_oklab,var(--danger)_30%,transparent)] text-danger hover:bg-[color-mix(in_oklab,var(--danger)_20%,transparent)]',
      },
      size: {
        md: 'h-control px-pad-x text-base',
        sm: 'h-control-sm px-2.5 text-sm [&_svg]:size-3.5',
        icon: 'h-control w-control p-0',
        'icon-sm': 'h-control-sm w-control-sm p-0 [&_svg]:size-3.5',
      },
    },
    defaultVariants: { variant: 'secondary', size: 'md' },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, loading, disabled, children, type = 'button', ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? <Loader2 className="animate-spin" aria-hidden /> : null}
      {children}
    </button>
  );
});

export { buttonVariants };
